import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Loader2 } from "lucide-react";
import { getCustomerDisplayName } from "@/lib/formatters";
import { ORDER_STATUS_LABELS } from "@/config/constants";
import L from "leaflet";
import "@/lib/leaflet-setup";
import { geocodeAddress } from "@/lib/geocoding";

type FulfillmentType = "delivery" | "installation" | "pickup" | "return";
type OrderType = "new_work" | "pickup_work" | "field_work";

const fulfillmentColors: Record<FulfillmentType, string> = {
  pickup: "#ef4444",     // Punainen - noudettava
  return: "#eab308",     // Keltainen - palautettava
  installation: "#22c55e", // Vihreä - asennus
  delivery: "#3b82f6",   // Sininen - toimitus
};

const fulfillmentLabels: Record<FulfillmentType, string> = {
  pickup: "Noudettava",
  return: "Palautettava",
  installation: "Asennus",
  delivery: "Toimitus",
};

const fulfillmentIcons: Record<FulfillmentType, string> = {
  pickup: "📦",
  return: "🔄",
  installation: "🔨",
  delivery: "🚚",
};

// Order type specific colors (priority over fulfillment type)
const getMarkerColor = (orderType: string, fulfillmentType: FulfillmentType): string => {
  if (orderType === "field_work") return "#a855f7"; // Purppura
  if (orderType === "pickup_work") return "#14b8a6"; // Turkoosi
  return fulfillmentColors[fulfillmentType];
};

const getMarkerIcon = (orderType: string, fulfillmentType: FulfillmentType): string => {
  if (orderType === "field_work") return "🔧";
  if (orderType === "pickup_work") return "🔄";
  return fulfillmentIcons[fulfillmentType];
};

const getMarkerLabel = (orderType: string, fulfillmentType: FulfillmentType): string => {
  if (orderType === "field_work") return "Kenttätyö";
  if (orderType === "pickup_work") return "Noudettava työ";
  return fulfillmentLabels[fulfillmentType];
};

const createColoredIcon = (color: string) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
};

// Status labels are now imported from @/config/constants

interface OrderWithCemetery {
  id: string;
  order_number: string;
  order_type: string;
  status: string;
  fulfillment_type: string;
  customers: {
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
  } | null;
  cemeteries: {
    id: string;
    name: string;
    address: string;
    postal_code: string;
    city: string;
    latitude: number | null;
    longitude: number | null;
  } | null;
}

interface MapMarker {
  orderId: string;
  orderNumber: string;
  customerName: string;
  cemeteryName: string;
  cemeteryAddress: string;
  status: string;
  fulfillmentType: FulfillmentType;
  orderType: string;
  lat: number;
  lng: number;
}

const KURIKKA_CENTER: [number, number] = [62.2584, 22.4094];

export default function OrdersMap() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const { data: orders, isLoading } = useQuery({
    queryKey: ["orders-for-map"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          id,
          order_number,
          order_type,
          status,
          fulfillment_type,
          customers (
            first_name,
            last_name,
            company_name
          ),
          cemeteries (
            id,
            name,
            address,
            postal_code,
            city,
            latitude,
            longitude
          )
        `)
        .is("deleted_at", null)
        .not("cemetery_id", "is", null)
        .limit(100);

      if (error) throw error;
      return data as OrderWithCemetery[];
    },
  });

  // Use centralized getCustomerDisplayName from formatters

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current).setView(KURIKKA_CENTER, 10);
    
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(mapRef.current);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Geocode orders and prepare markers
  useEffect(() => {
    if (!orders) return;

    const geocodeOrders = async () => {
      setIsGeocoding(true);
      const markers: MapMarker[] = [];

      for (const order of orders) {
        if (!order.cemeteries) continue;

        const cemetery = order.cemeteries;
        let lat = cemetery.latitude;
        let lng = cemetery.longitude;

        if (lat === null || lng === null) {
          const coords = await geocodeAddress(cemetery.address, cemetery.postal_code, cemetery.city);
          if (coords) {
            lat = coords.lat;
            lng = coords.lng;
            await supabase
              .from("cemeteries")
              .update({ latitude: lat, longitude: lng })
              .eq("id", cemetery.id);
          }
        }

        if (lat !== null && lng !== null) {
          const fulfillmentType = order.fulfillment_type as FulfillmentType;
          markers.push({
            orderId: order.id,
            orderNumber: order.order_number,
            customerName: getCustomerDisplayName(order.customers),
            cemeteryName: cemetery.name,
            cemeteryAddress: `${cemetery.address}, ${cemetery.postal_code} ${cemetery.city}`,
            status: order.status,
            fulfillmentType,
            orderType: order.order_type,
            lat,
            lng,
          });
        }
      }

      setMapMarkers(markers);
      setIsGeocoding(false);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    };

    geocodeOrders();
  }, [orders, queryClient]);

  // Update markers on map with clustering
  useEffect(() => {
    if (!mapRef.current) return;

    // Remove old cluster group
    if (clusterGroupRef.current) {
      mapRef.current.removeLayer(clusterGroupRef.current);
      clusterGroupRef.current = null;
    }

    if (mapMarkers.length === 0) return;

    // Create new cluster group
    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 40,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    });

    // Add markers to cluster group
    mapMarkers.forEach(markerData => {
      const color = getMarkerColor(markerData.orderType, markerData.fulfillmentType);
      const icon = getMarkerIcon(markerData.orderType, markerData.fulfillmentType);
      const label = getMarkerLabel(markerData.orderType, markerData.fulfillmentType);

      const marker = L.marker([markerData.lat, markerData.lng], {
        icon: createColoredIcon(color),
      });

      marker.bindPopup(`
        <div style="min-width: 150px;">
          <p style="font-weight: bold; margin: 0 0 4px 0;">${markerData.orderNumber}</p>
          <p style="font-size: 0.875rem; margin: 0 0 4px 0;">${icon} ${label}</p>
          <p style="font-size: 0.875rem; margin: 0 0 4px 0;">${markerData.customerName}</p>
          <p style="font-size: 0.875rem; font-weight: 500; margin: 0 0 4px 0;">${markerData.cemeteryName}</p>
          <p style="font-size: 0.75rem; color: #666; margin: 0 0 4px 0;">${markerData.cemeteryAddress}</p>
          <p style="font-size: 0.75rem; margin: 0;">Tila: ${ORDER_STATUS_LABELS[markerData.status] || markerData.status}</p>
        </div>
      `);

      clusterGroup.addLayer(marker);
    });

    mapRef.current.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;

    // Fit bounds to show all markers
    const bounds = L.latLngBounds(mapMarkers.map(m => [m.lat, m.lng]));
    mapRef.current.fitBounds(bounds, { padding: [50, 50] });
  }, [mapMarkers]);

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate("/orders")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Takaisin tilauksiin
            </Button>
            <h1 className="text-2xl font-bold">Tilaukset kartalla</h1>
          </div>
          <span className="text-sm text-muted-foreground">
            {mapMarkers.length} tilausta kartalla
          </span>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 p-3 bg-muted/50 rounded-lg">
          <span className="text-sm font-medium">Selite:</span>
          {/* Order types with priority */}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-background" style={{ backgroundColor: "#a855f7" }} />
            <span className="text-sm">🔧 Kenttätyö</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-background" style={{ backgroundColor: "#14b8a6" }} />
            <span className="text-sm">🔄 Noudettava työ</span>
          </div>
          {/* Standard fulfillment types for new_work */}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-background" style={{ backgroundColor: "#22c55e" }} />
            <span className="text-sm">🔨 Asennus</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-background" style={{ backgroundColor: "#3b82f6" }} />
            <span className="text-sm">🚚 Toimitus</span>
          </div>
        </div>

        {/* Map */}
        <div className="h-[calc(100vh-280px)] rounded-lg overflow-hidden border relative">
          {(isLoading || isGeocoding) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 z-10 gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-muted-foreground">
                {isLoading ? "Ladataan tilauksia..." : "Haetaan sijainteja osoitteista..."}
              </span>
            </div>
          )}
          <div ref={mapContainerRef} className="h-full w-full isolate" />
          {!isLoading && !isGeocoding && mapMarkers.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
              Ei tilauksia näytettäväksi kartalla
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
